/**
 * PaymentDialog — portable usage-based credit purchasing dialog.
 *
 * Drop this into any project that has the /api/billing/* endpoints.
 * Uses bank transfer (SEPA / ACH) via Cenoa / Hawala virtual accounts —
 * no card processing required.
 *
 * ┌──────────────────────────────────────────────────────────────┐
 * │  Props                                                        │
 * │  ─────────────────────────────────────────────────────────── │
 * │  open              bool       dialog open state              │
 * │  onClose           fn         called on close                │
 * │                                                              │
 * │  productName       string     e.g. "AI Writing Credits"      │
 * │  productDescription string    shown in the header            │
 * │  unitLabel         string     e.g. "credits" (default)       │
 * │  costPerOperation  number     credits per operation (display) │
 * │  operationLabel    string     e.g. "section generation"      │
 * │                                                              │
 * │  currentBalance    number     shown in header                │
 * │  onSuccess         fn(newBalance) called after submission    │
 * │                                                              │
 * │  packages          array|null if null, fetched from API      │
 * │  apiBase           string     default "/api/billing"         │
 * └──────────────────────────────────────────────────────────────┘
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Tab,
  Tabs,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import {
  Bolt as BoltIcon,
  AccountBalance as BankIcon,
  History as HistoryIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';

import CreditPackageSelector from './CreditPackageSelector';
import BankTransferForm from './BankTransferForm';
import TransactionHistory from './TransactionHistory';
import { getPackages, getTransactions, createTransferRequest, notifyTransferSubmitted } from './billingApi';

const DEFAULT_BASE = '/billing';

const TabPanel = ({ value, index, children }) =>
  value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

const PaymentDialog = ({
  open,
  onClose,

  productName = 'Credits',
  productDescription = 'Purchase credits to use this feature.',
  unitLabel = 'credits',
  costPerOperation = 1,
  operationLabel = 'operation',

  currentBalance = 0,
  onSuccess,

  packages: packagesProp = null,
  apiBase = DEFAULT_BASE,
}) => {
  const [tab, setTab] = useState(0);

  const [packages, setPackages] = useState(packagesProp || []);
  const [packagesLoading, setPackagesLoading] = useState(!packagesProp);
  const [selectedPackageId, setSelectedPackageId] = useState(null);

  // Transfer request state
  const [transferData, setTransferData] = useState(null); // { reference_code, bank_details, purchase_id, ... }
  const [preparingTransfer, setPreparingTransfer] = useState(false);
  const [transferError, setTransferError] = useState(null);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false); // user clicked "I've made the transfer"

  // Transaction history
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setTab(0);
    setSelectedPackageId(null);
    setTransferData(null);
    setTransferError(null);
    setSubmitted(false);

    if (!packagesProp) {
      setPackagesLoading(true);
      getPackages(apiBase).then(r => {
        if (r.ok) setPackages(r.data);
        setPackagesLoading(false);
      });
    }
  }, [open, packagesProp, apiBase]);

  // Load history when tab opens
  useEffect(() => {
    if (tab !== 1 || !open) return;
    setTxLoading(true);
    getTransactions(apiBase).then(r => {
      if (r.ok) setTransactions(r.data);
      setTxLoading(false);
    });
  }, [tab, open, apiBase]);

  const handleSelectPackage = useCallback(async (pkgId) => {
    setSelectedPackageId(pkgId);
    setTransferData(null);
    setTransferError(null);
    setPreparingTransfer(true);

    const result = await createTransferRequest(pkgId, apiBase);
    if (result.ok) {
      setTransferData(result.data);
    } else {
      setTransferError(result.error);
    }
    setPreparingTransfer(false);
  }, [apiBase]);

  const handleTransferSubmitted = useCallback(async (purchaseId) => {
    const result = await notifyTransferSubmitted(purchaseId, apiBase);
    if (!result.ok) throw new Error(result.error);
    setSubmitting(false);
    setSubmitted(true);
    onSuccess?.(currentBalance); // balance unchanged until admin confirms
  }, [apiBase, currentBalance, onSuccess]);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const selectedPackage = packages.find(p => p.id === selectedPackageId);

  // ── Submitted / pending confirmation screen ─────────────────────
  if (submitted && transferData) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <PendingIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>Transfer notification received</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We'll add <strong>{selectedPackage?.credits} {unitLabel}</strong> to your account
            once the transfer is verified — usually within 1 business day.
          </Typography>
          <Chip
            icon={<BankIcon />}
            label={`Ref: ${transferData.reference_code}`}
            variant="outlined"
            sx={{ fontFamily: 'monospace', fontSize: 13 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleClose} fullWidth sx={{ mx: 2, mb: 1 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Main dialog ─────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <BankIcon color="primary" />
            <Box>
              <Typography variant="h6" lineHeight={1.2}>{productName}</Typography>
              <Typography variant="caption" color="text.secondary">{productDescription}</Typography>
            </Box>
          </Stack>
          <Chip
            icon={<BoltIcon />}
            label={`${currentBalance} ${unitLabel}`}
            size="small"
            color={currentBalance === 0 ? 'error' : currentBalance < 5 ? 'warning' : 'default'}
            variant="outlined"
          />
        </Stack>

        {costPerOperation > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Cost: {costPerOperation} {unitLabel} per {operationLabel}
          </Typography>
        )}
      </DialogTitle>

      <Divider sx={{ mt: 1.5 }} />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab icon={<BoltIcon />} label="Add Credits" iconPosition="start" />
          <Tab icon={<HistoryIcon />} label="History" iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 1 }}>
        {/* ── Add Credits tab ──────────────────────────────────── */}
        <TabPanel value={tab} index={0}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a credit package, then follow the bank transfer instructions.
          </Typography>

          <CreditPackageSelector
            packages={packages}
            selected={selectedPackageId}
            onSelect={handleSelectPackage}
            loading={packagesLoading}
            currency="EUR"
          />

          {transferError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setTransferError(null)}>
              {transferError}
            </Alert>
          )}

          {selectedPackageId && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Transfer instructions
              </Typography>

              {preparingTransfer ? (
                <Typography variant="body2" color="text.secondary">
                  Generating transfer reference…
                </Typography>
              ) : transferData ? (
                <BankTransferForm
                  referenceCode={transferData.reference_code}
                  bankDetails={transferData.bank_details}
                  pkg={selectedPackage}
                  purchaseId={transferData.purchase_id}
                  onSubmitted={handleTransferSubmitted}
                  submitting={submitting}
                  setSubmitting={setSubmitting}
                />
              ) : null}
            </Box>
          )}
        </TabPanel>

        {/* ── History tab ───────────────────────────────────────── */}
        <TabPanel value={tab} index={1}>
          <TransactionHistory transactions={transactions} loading={txLoading} />
        </TabPanel>
      </DialogContent>

      {tab === 0 && !selectedPackageId && (
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PaymentDialog;
